import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PublicBoxDataSourceService } from '../../services/public-box-data-source.service';
import { isBrowserContext, isNil, isNotEmptyArray, Nillable } from '@web/utils';
import { SpinnerFilledComponent } from '@web/shared/ui';
import { EMPTY, map, Observable, switchMap } from 'rxjs';
import {
  activatedRouteQueryParamAsString,
  NavigatorService,
  RouteQueryParam,
} from '@web/common';
import {
  ActivatedRoute,
  Params,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PublicBoxCategory } from '../../models/public-box-category';

@Component({
  selector: 'app-public-box-categories',
  imports: [CommonModule, SpinnerFilledComponent, RouterLink, RouterLinkActive],
  templateUrl: './public-box-categories.component.html',
  styleUrl: './public-box-categories.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicBoxCategoriesComponent {
  private readonly publicBoxDataSource = inject(PublicBoxDataSourceService);
  private readonly navigator = inject(NavigatorService);

  public readonly chooseCategoryEvent = output<void>();

  protected readonly currentCategory$ = this.currentCategorySource();
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly isBrowserContext = isBrowserContext();
  protected readonly isNotEmptyArray = isNotEmptyArray;
  protected readonly categories$ = this.categoriesSource();

  private readonly destroyRef = inject(DestroyRef);

  public constructor() {
    this.navigateToDefaultCategory();
  }

  public handleChooseCategoryClick(): void {
    this.chooseCategoryEvent.emit();
  }

  protected categoryQueryParam(slug: string): Params {
    return {
      [RouteQueryParam.PL_Category]: slug,
    };
  }

  private categoriesSource(): Observable<PublicBoxCategory[]> {
    return this.publicBoxDataSource.fetchCategories();
  }

  private navigateToDefaultCategory(): void {
    this.currentCategory$
      .pipe(
        switchMap((category) =>
          isNil(category)
            ? this.categories$.pipe(map(([{ slug }]) => slug))
            : EMPTY,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((slug) => {
        this.navigator.changeQueryParams(this.activatedRoute, {
          queryParams: this.categoryQueryParam(slug),
        });
      });
  }

  private currentCategorySource(): Observable<Nillable<string>> {
    return activatedRouteQueryParamAsString(RouteQueryParam.PL_Category);
  }
}
